// Importing dependencies
import { motion } from "framer-motion";
import AOS from "aos";
import { useEffect } from "react";
import "aos/dist/aos.css"; // AOS CSS import for animation
import logo from '../assets/images/logo.png';

// Hero Section Component
export default function HeroSection() {
  useEffect(() => {
    AOS.init({ duration: 1200, once: false, delay: 20 });
  }, []);

  return (
    <div className="relative min-h-[340px] md:min-h-[70vh] w-full bg-black overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-[#060b22] to-[#020617] opacity-95"></div>

      {/* Floating Animated Blobs */}
      <motion.div
        className="absolute top-12 left-16 w-56 h-56 bg-[#1a237e] rounded-full opacity-30 blur-3xl"
        animate={{ scale: [1, 1.5, 1], x: [0, 30, -30, 0], y: [0, -30, 30, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      ></motion.div>

      <motion.div
        className="absolute bottom-16 right-10 w-72 h-72 bg-[#0d47a1] rounded-full opacity-30 blur-3xl"
        animate={{ scale: [1, 1.3, 1], x: [0, -40, 40, 0], y: [0, 40, -40, 0] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
      ></motion.div>

      {/* Floating Particles */}
      {Array.from({ length: 15 }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 bg-gray-300 rounded-full opacity-60"
          style={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
          }}
          animate={{ y: [-20, 20, -20] }}
          transition={{ duration: Math.random() * 4 + 2, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}

      {/* Content Wrapper - Mobile Stacked, Desktop Side-by-Side */}
      <div className="relative z-10 h-full min-h-[340px] md:min-h-[70vh] flex flex-col md:flex-row items-center md:items-center justify-center md:justify-between w-full px-4 md:px-8 lg:px-16 py-6 md:py-0 gap-4 md:gap-0">
        {/* Main Heading and Text */}
        <div className="text-center md:text-left max-w-2xl">
          <h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-100 leading-tight mb-3 md:mb-6 tracking-wide"
            data-aos="fade-right"
          >
            Empowering Businesses with
            <br />
            <span className="bg-gradient-to-r from-blue-500 to-indigo-600 bg-clip-text text-transparent">
              Right Talent
            </span>
          </h1>

          {/* CTA Button */}
          <motion.div
            className="flex justify-center md:justify-start"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <motion.button
              className="bg-gradient-to-r from-[#0d47a1] to-[#1a237e] hover:from-[#0b376b] hover:to-[#101c51] text-white text-sm md:text-lg font-medium px-6 md:px-8 py-2.5 md:py-4 rounded-full shadow-2xl transition-transform duration-300 transform hover:scale-110"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              data-aos="zoom-in"
            >
              Get Started Today
            </motion.button>
          </motion.div>
        </div>

        {/* Floating Logo - Reduced Mobile Size */}
        <motion.img
          src={logo}
          alt="Company Logo"
          className="w-32 h-32 sm:w-40 sm:h-40 md:w-64 md:h-64 lg:w-72 lg:h-72 object-contain"
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
      </div>
    </div>
  );
}